import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import "@/App.css";

// Import pages
import LandingPage from "@/pages/LandingPage";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import LoadingPage from "@/pages/LoadingPage";
import StrategyDetailsPage from "@/pages/StrategyDetailsPage";
import LiveTrading from "@/pages/LiveTrading";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios globally
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSessionId(sessionId);
    } else {
      checkExistingSession();
    }
  }, []);

  const processSessionId = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      }, {
        withCredentials: true
      });

      setUser(response.data.user);

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      toast.success("Logged in successfully!");
    } catch (error) {
      console.error("Failed to process session:", error);
      toast.error("Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.log("No existing session");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? (user.role === "admin" ? <Navigate to="/admin" /> : <Navigate to="/dashboard" />) : <LandingPage />} />
          <Route path="/dashboard" element={user && user.role === "client" ? <ClientDashboard /> : <Navigate to="/" />} />
          <Route path="/admin" element={user && user.role === "admin" ? <AdminDashboard /> : <Navigate to="/" />} />
          <Route path="/live-trading" element={user ? <LiveTrading /> : <Navigate to="/" />} />
          <Route path="/strategy/:strategyId" element={user ? <StrategyDetailsPage /> : <Navigate to="/" />} />
        </Routes>
      </BrowserRouter>

      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
  );
}

export default App;
